import { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { markNotificationRead, fetchNotifiesThunk, markAllReadLocal } from "../../../Slices/socketemail";
import { markReplyNotificationRead, markAllReplyNotificationsRead } from "../../../Slices/emailRepliesSlice";
import { Badge, Dropdown, List, Card, Typography, Tabs, Button } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faReply, faBell, faCheckDouble } from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
dayjs.extend(relativeTime);

const { Text } = Typography;

const Notify = () => {
    const navigate   = useNavigate();
    const dispatch   = useDispatch();
    const [open, setOpen] = useState(false);

    const notifications      = useSelector((state) => state.emailSocket.notificationData);
    const unreadCount        = useSelector((state) => state.emailSocket.unreadCount);
    const totalNotifications = useSelector((state) => state.emailSocket.totalNotifications);
    const notificationPage   = useSelector((state) => state.emailSocket.notificationPage);
    const replyNotifications = useSelector((state) => state.emailReplies.replyNotifications);
    const loginUser          = useSelector((state) => state?.loginUserInfo?.loginUserDetails);

    const unreadReplies = replyNotifications.filter(n => !n.read).length;
    const totalCount    = unreadCount + unreadReplies;

    const handleClick = (item) => {
        // Mark this single notification as read
        if (!item.isRead) {
            dispatch(markNotificationRead({ userId: loginUser.id, notificationId: item.id }));
        }
        // Navigate based on type
        if (item.type === 'sequence' && item.leadId) {
            navigate(`/sales/${item.leadId}`);
            setOpen(false);
            return;
        }
        const routes = {
            leave:              '/adminleaves',
            'Job Requisition':  '/JobPosting',
            followup:           '/sales',
            workflow:           '/sales',
        };
        const route = routes[item.type];
        if (route) navigate(route);
        setOpen(false);
    };

    const handleMarkOneRead = (e, item) => {
        e.stopPropagation();
        dispatch(markNotificationRead({ userId: loginUser.id, notificationId: item.id }));
    };

    const handleMarkAllGeneralRead = () => {
        dispatch(markNotificationRead({ userId: loginUser.id }));
        dispatch(markAllReadLocal());
    };

    const handleReplyClick = (notification) => {
        dispatch(markReplyNotificationRead({ id: notification.id }));
        if (notification.leadId) {
            navigate(`/sales/${notification.leadId}`);
        }
        setOpen(false);
    };

    const handleMarkAllRepliesRead = () => {
        dispatch(markAllReplyNotificationsRead());
    };

    const handleLoadMore = () => {
        if (notifications.length < totalNotifications) {
            dispatch(fetchNotifiesThunk({ userId: loginUser.id, page: notificationPage + 1, limit: 50 }));
        }
    };

    const tabItems = [
        {
            key: 'general',
            label: (
                <span>
                    General
                    {unreadCount > 0 && (
                        <Badge count={unreadCount} size="small" style={{ marginLeft: 6 }} />
                    )}
                </span>
            ),
            children: (
                <div>
                    {/* Mark all as read header */}
                    {unreadCount > 0 && (
                        <div
                            style={{ padding: '6px 16px', borderBottom: '1px solid #f0f0f0', background: '#fafafa', display: 'flex', justifyContent: 'flex-end' }}
                        >
                            <span
                                style={{ fontSize: 11, color: '#6366f1', cursor: 'pointer', fontWeight: 500 }}
                                onClick={handleMarkAllGeneralRead}
                            >
                                <FontAwesomeIcon icon={faCheckDouble} style={{ marginRight: 4 }} />
                                Mark all as read
                            </span>
                        </div>
                    )}
                    <div style={{ maxHeight: 350, overflowY: 'auto' }}>
                        {notifications.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: 16, color: '#999' }}>
                                No notifications
                            </div>
                        ) : (
                            <>
                                <List
                                    dataSource={notifications}
                                    renderItem={(item) => (
                                        <List.Item
                                            key={item.id}
                                            style={{
                                                padding: '10px 16px',
                                                borderBottom: '1px solid #f0f0f0',
                                                cursor: 'pointer',
                                                background: item.isRead ? '#fff' : '#f0f7ff',
                                                borderLeft: item.isRead ? 'none' : '3px solid #3b82f6',
                                                transition: 'background 0.2s',
                                            }}
                                            onClick={() => handleClick(item)}
                                        >
                                            <div style={{ flex: 1 }}>
                                                <Text style={{ fontWeight: item.isRead ? 400 : 600, color: item.isRead ? '#6b7280' : '#0f172a', fontSize: 13 }}>
                                                    {item.message}
                                                </Text>
                                                <br />
                                                <Text type="secondary" style={{ fontSize: 11 }}>
                                                    {dayjs(item.createdAt).format('DD MMM YYYY, h:mm A')}
                                                </Text>
                                            </div>
                                            {!item.isRead && (
                                                <i
                                                    className="fa-regular fa-circle-check"
                                                    style={{ color: '#4CAF50', fontSize: 16, marginLeft: 10, cursor: 'pointer' }}
                                                    title="Mark as read"
                                                    onClick={(e) => handleMarkOneRead(e, item)}
                                                />
                                            )}
                                        </List.Item>
                                    )}
                                />
                                {/* Load more */}
                                {notifications.length < totalNotifications && (
                                    <div style={{ textAlign: 'center', padding: '8px 0', borderTop: '1px solid #f0f0f0' }}>
                                        <Button type="link" size="small" onClick={handleLoadMore} style={{ fontSize: 12 }}>
                                            Load older notifications ({totalNotifications - notifications.length} more)
                                        </Button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            ),
        },
        {
            key: 'replies',
            label: (
                <span>
                    <FontAwesomeIcon icon={faReply} style={{ marginRight: 5, color: '#8b5cf6' }} />
                    Replies
                    {unreadReplies > 0 && (
                        <Badge count={unreadReplies} size="small" style={{ marginLeft: 6, backgroundColor: '#8b5cf6' }} />
                    )}
                </span>
            ),
            children: (
                <div>
                    {replyNotifications.length > 0 && unreadReplies > 0 && (
                        <div
                            style={{ padding: '6px 16px', borderBottom: '1px solid #f0f0f0', background: '#fafafa', cursor: 'pointer', fontSize: 12, color: '#6366f1', textAlign: 'right' }}
                            onClick={handleMarkAllRepliesRead}
                        >
                            Mark all as read
                        </div>
                    )}
                    <div style={{ maxHeight: 350, overflowY: 'auto' }}>
                        {replyNotifications.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: 16, color: '#999' }}>
                                No replies yet
                            </div>
                        ) : (
                            <List
                                dataSource={replyNotifications}
                                renderItem={(item) => (
                                    <List.Item
                                        key={item.id}
                                        style={{
                                            padding: '10px 16px',
                                            borderBottom: '1px solid #f0f0f0',
                                            cursor: 'pointer',
                                            background: item.read ? '#fff' : '#f5f3ff',
                                            borderLeft: item.read ? 'none' : '3px solid #8b5cf6',
                                        }}
                                        onClick={() => handleReplyClick(item)}
                                    >
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <FontAwesomeIcon icon={faReply} style={{ color: '#8b5cf6', fontSize: 11, flexShrink: 0 }} />
                                                <Text strong style={{ fontSize: 12, color: '#0f172a' }} ellipsis>
                                                    {item.from || 'Unknown sender'}
                                                </Text>
                                            </div>
                                            <Text style={{ fontSize: 12, color: '#475569', display: 'block', marginTop: 2 }} ellipsis>
                                                {item.subject || '(no subject)'}
                                            </Text>
                                            <Text type="secondary" style={{ fontSize: 11 }}>
                                                {dayjs(item.receivedAt).fromNow()}
                                            </Text>
                                        </div>
                                    </List.Item>
                                )}
                            />
                        )}
                    </div>
                </div>
            ),
        },
    ];

    const dropdownContent = (
        <Card
            bordered={false}
            style={{ width: 380, padding: 0, boxShadow: '0 8px 32px rgba(0,0,0,0.12)', borderRadius: 12 }}
            bodyStyle={{ padding: 0 }}
        >
            <div style={{ padding: '10px 16px', borderBottom: '1px solid #f0f0f0', background: '#fafafa', fontWeight: 600, fontSize: 14, borderRadius: '12px 12px 0 0' }}>
                Notifications
            </div>
            <Tabs
                defaultActiveKey="general"
                items={tabItems}
                size="small"
                style={{ padding: '0 8px' }}
                tabBarStyle={{ marginBottom: 0 }}
            />
        </Card>
    );

    return (
        <Dropdown
            overlay={dropdownContent}
            trigger={["click"]}
            open={open}
            onOpenChange={(flag) => setOpen(flag)}
            placement="bottomRight"
        >
            <Badge count={totalCount} overflowCount={99} size="small">
                <FontAwesomeIcon
                    icon={faBell}
                    className={totalCount > 0 ? "fa-shake" : ""}
                    style={{ fontSize: 22, cursor: 'pointer', color: 'white' }}
                />
            </Badge>
        </Dropdown>
    );
};

export default Notify;
